import dagre from "@dagrejs/dagre";

/**
 * Denbaya — Traoré extended family (≈50+ people)
 * Each person has: id, firstName, middleName?, lastName, nickname?, birthYear, birthDate (YYYY-MM-DD), deathYear,
 * gender, phone, email, address, city, photo (initials fallback),
 * `spouseId` (monogamie) ou `unions: [{ spouseId, order? }]` (polygamie) ;
 * `parentIds` ; `notes`
 */
/**
 * Initial demo data — used when no tree is loaded from the database.
 * The active people array lives in the Zustand store; family.js reads a
 * synchronised module-level reference (`_people`) so internal helpers
 * don't need an extra parameter.
 */
export const DEMO_PEOPLE = [
  {
    id: "ibrahima",
    firstName: "Ibrahima",
    lastName: "Traoré",
    birthYear: 1928,
    birthDate: "1928-06-15",
    deathYear: 2001,
    gender: "male",
    phone: null,
    email: null,
    address: "Ancienne adresse, Ségou",
    city: "Ségou",
    notes: "Patriarche de la famille. Cultivateur de coton. Polygame : 2e union à Bamako (années 60).",
    // Polygynie (Mali) : plusieurs épouses, ordre de préséance
    unions: [
      { spouseId: "mariam", order: 1 },
      { spouseId: "siraDiallo", order: 2 },
    ],
    parentIds: [],
  },
  {
    id: "mariam",
    firstName: "Mariam",
    lastName: "Coulibaly",
    birthYear: 1932,
    deathYear: 2008,
    gender: "female",
    phone: null,
    email: null,
    address: "Ancienne adresse, Ségou",
    city: "Ségou",
    notes: "Matriarche. Connue pour sa sagesse et ses contes. Première épouse.",
    spouseId: "ibrahima",
    parentIds: [],
  },
  {
    id: "siraDiallo",
    firstName: "Sira",
    lastName: "Diallo",
    birthYear: 1940,
    deathYear: 2015,
    gender: "female",
    phone: null,
    email: null,
    address: "Quartier Médina Coura, Bamako",
    city: "Bamako",
    notes: "Deuxième épouse. Tisserande et mère de Yaya (branche cadette Ségou).",
    spouseId: "ibrahima",
    parentIds: [],
  },
  {
    id: "seydou",
    firstName: "Seydou",
    lastName: "Traoré",
    birthYear: 1955,
    deathYear: null,
    gender: "male",
    phone: "+223 76 98 76 54",
    email: "seydou.traore@gmail.com",
    address: "Rue 321, Hamdallaye",
    city: "Bamako",
    notes: "Aîné. Enseignant à la retraite.",
    spouseId: "awa",
    parentIds: ["ibrahima", "mariam"],
  },
  {
    id: "awa",
    firstName: "Awa",
    lastName: "Sanogo",
    birthYear: 1958,
    deathYear: null,
    gender: "female",
    phone: "+223 65 44 21 10",
    email: null,
    address: "Rue 321, Hamdallaye",
    city: "Bamako",
    notes: "Commerçante au Grand Marché.",
    spouseId: "seydou",
    parentIds: [],
  },
  {
    id: "amadou",
    firstName: "Amadou",
    middleName: "Ibrahim",
    lastName: "Traoré",
    nickname: "Doums",
    birthYear: 1952,
    deathYear: null,
    gender: "male",
    phone: "+223 76 12 34 56",
    email: "amadou@traore.ml",
    address: "Quartier Hippodrome, Rue 42",
    city: "Bamako",
    notes: "Deuxième fils. Ancien fonctionnaire au Ministère des Finances.",
    spouseId: "nana",
    parentIds: ["ibrahima", "mariam"],
  },
  {
    id: "nana",
    firstName: "Nana",
    lastName: "Konaté",
    birthYear: 1956,
    deathYear: null,
    gender: "female",
    phone: "+223 79 55 43 21",
    email: "nana.konate@yahoo.fr",
    address: "Quartier Hippodrome, Rue 42",
    city: "Bamako",
    notes: "Infirmière au CHU Gabriel Touré.",
    spouseId: "amadou",
    parentIds: [],
  },
  {
    id: "djeneba",
    firstName: "Djeneba",
    lastName: "Traoré",
    birthYear: 1960,
    deathYear: null,
    gender: "female",
    phone: "+223 66 87 65 43",
    email: "djeneba.t@hotmail.com",
    address: "ACI 2000, Immeuble Kebe",
    city: "Bamako",
    notes: "Cadette. Directrice d'une école primaire.",
    spouseId: "bakary",
    parentIds: ["ibrahima", "mariam"],
  },
  {
    id: "bakary",
    firstName: "Bakary",
    lastName: "Diallo",
    birthYear: 1957,
    deathYear: null,
    gender: "male",
    phone: "+223 76 22 33 44",
    email: null,
    address: "ACI 2000, Immeuble Kebe",
    city: "Bamako",
    notes: "Architecte.",
    spouseId: "djeneba",
    parentIds: [],
  },
  {
    id: "issaka",
    firstName: "Issaka",
    lastName: "Traoré",
    birthYear: 1962,
    deathYear: null,
    gender: "male",
    phone: "+223 76 90 12 34",
    email: null,
    address: "Kalaban-Coura",
    city: "Bamako",
    notes: "Oncle paternel. Mécanicien puis taxi.",
    spouseId: "assetouKonate",
    parentIds: ["ibrahima", "mariam"],
  },
  {
    id: "assetouKonate",
    firstName: "Assetou",
    lastName: "Konaté",
    birthYear: 1964,
    deathYear: null,
    gender: "female",
    phone: "+223 65 11 22 33",
    email: null,
    address: "Kalaban-Coura",
    city: "Bamako",
    notes: "Tante par alliance. Vendeuse au marché.",
    spouseId: "issaka",
    parentIds: [],
  },
  {
    id: "fanta",
    firstName: "Fanta",
    lastName: "Traoré",
    birthYear: 1965,
    deathYear: null,
    gender: "female",
    phone: "+223 66 77 88 99",
    email: "fanta.traore@yahoo.fr",
    address: "Sotuba",
    city: "Bamako",
    notes: "Tante cadette. Sage-femme retraitée.",
    spouseId: "modibo",
    parentIds: ["ibrahima", "mariam"],
  },
  {
    id: "yaya",
    firstName: "Yaya",
    lastName: "Traoré",
    birthYear: 1967,
    deathYear: null,
    gender: "male",
    phone: "+223 76 11 00 99",
    email: null,
    address: "Badalabougou",
    city: "Bamako",
    notes: "Fils d'Ibrahima et de Sira (2e union). Commerçant au grand marché.",
    spouseId: null,
    parentIds: ["ibrahima", "siraDiallo"],
  },
  {
    id: "modibo",
    firstName: "Modibo",
    lastName: "Keïta",
    birthYear: 1963,
    deathYear: null,
    gender: "male",
    phone: "+223 76 44 55 66",
    email: null,
    address: "Sotuba",
    city: "Bamako",
    notes: "Taxi-ville et conseiller de quartier.",
    spouseId: "fanta",
    parentIds: [],
  },
  {
    id: "omar",
    firstName: "Omar",
    lastName: "Traoré",
    birthYear: 1978,
    deathYear: null,
    gender: "male",
    phone: "+223 78 11 22 33",
    email: "omar.traore@outlook.com",
    address: "Magnambougou",
    city: "Bamako",
    notes: "Fils de Seydou. Ingénieur télécom.",
    spouseId: "aicha",
    parentIds: ["seydou", "awa"],
  },
  {
    id: "safiatou",
    firstName: "Safiatou",
    lastName: "Traoré",
    birthYear: 1980,
    deathYear: null,
    gender: "female",
    phone: "+223 66 55 44 33",
    email: null,
    address: "Lafiabougou",
    city: "Bamako",
    notes: "Fille de Seydou. Médecin généraliste.",
    spouseId: "karim",
    parentIds: ["seydou", "awa"],
  },
  {
    id: "binta",
    firstName: "Binta",
    lastName: "Traoré",
    birthYear: 1982,
    deathYear: null,
    gender: "female",
    phone: "+223 78 22 11 00",
    email: null,
    address: "Hamdallaye",
    city: "Bamako",
    notes: "Fille de Seydou. Pharmacienne.",
    spouseId: "idrissaB",
    parentIds: ["seydou", "awa"],
  },
  {
    id: "youssouf",
    firstName: "Youssouf",
    lastName: "Traoré",
    birthYear: 1984,
    deathYear: null,
    gender: "male",
    phone: "+223 76 33 44 55",
    email: "youssouf.t@gmail.com",
    address: "Magnambougou",
    city: "Bamako",
    notes: "Fils de Seydou. Pilote de ligne.",
    spouseId: "ramatou",
    parentIds: ["seydou", "awa"],
  },
  {
    id: "fatima",
    firstName: "Fatima",
    lastName: "Traoré",
    birthYear: 1980,
    deathYear: null,
    gender: "female",
    phone: "+223 76 45 67 89",
    email: "fatima.traore@gmail.com",
    address: "Badalabougou",
    city: "Bamako",
    notes: "Fille d'Amadou. Avocate.",
    spouseId: "drissa",
    parentIds: ["amadou", "nana"],
  },
  {
    id: "drissa",
    firstName: "Drissa",
    lastName: "Coulibaly",
    birthYear: 1977,
    deathYear: null,
    gender: "male",
    phone: "+223 79 88 77 66",
    email: null,
    address: "Badalabougou",
    city: "Bamako",
    notes: "Comptable.",
    spouseId: "fatima",
    parentIds: [],
  },
  {
    id: "moussa",
    firstName: "Moussa",
    lastName: "Traoré",
    birthYear: 1983,
    deathYear: null,
    gender: "male",
    phone: "+223 77 34 56 78",
    email: "moussa.t@gmail.com",
    address: "Quinzambougou",
    city: "Bamako",
    notes: "Fils d'Amadou. Développeur logiciel.",
    spouseId: "aminata",
    parentIds: ["amadou", "nana"],
  },
  {
    id: "aminata",
    firstName: "Aminata",
    lastName: "Diarra",
    birthYear: 1985,
    deathYear: null,
    gender: "female",
    phone: "+223 66 23 45 67",
    email: "aminata.diarra@gmail.com",
    address: "Quinzambougou",
    city: "Bamako",
    notes: "Enseignante universitaire.",
    spouseId: "moussa",
    parentIds: [],
  },
  {
    id: "hawa",
    firstName: "Hawa",
    lastName: "Traoré",
    birthYear: 1988,
    deathYear: null,
    gender: "female",
    phone: "+223 77 88 99 00",
    email: "hawa.traore@gmail.com",
    address: "Hippodrome",
    city: "Bamako",
    notes: "Fille d'Amadou. Journaliste.",
    spouseId: "mamoudou",
    parentIds: ["amadou", "nana"],
  },
  {
    id: "cheick",
    firstName: "Cheick",
    lastName: "Traoré",
    birthYear: 1990,
    deathYear: null,
    gender: "male",
    phone: "+223 76 55 66 77",
    email: null,
    address: "ACI 2000",
    city: "Bamako",
    notes: "Fils d'Amadou. Électricien.",
    spouseId: "salimataT",
    parentIds: ["amadou", "nana"],
  },
  {
    id: "kenedi",
    firstName: "Kenedi",
    lastName: "Diallo",
    birthYear: 1989,
    deathYear: null,
    gender: "male",
    phone: "+223 78 00 11 22",
    email: "kenedi.d@gmail.com",
    address: "ACI 2000",
    city: "Bamako",
    notes: "Fils de Djeneba. Cousin issu de germains.",
    spouseId: "assetouKen",
    parentIds: ["djeneba", "bakary"],
  },
  {
    id: "rokia",
    firstName: "Rokia",
    lastName: "Diallo",
    birthYear: 1992,
    deathYear: null,
    gender: "female",
    phone: "+223 66 44 33 22",
    email: null,
    address: "ACI 2000",
    city: "Bamako",
    notes: "Fille de Djeneba. Étudiante en droit.",
    spouseId: "issaRokia",
    parentIds: ["djeneba", "bakary"],
  },
  {
    id: "souleymane",
    firstName: "Souleymane",
    lastName: "Diallo",
    birthYear: 1995,
    deathYear: null,
    gender: "male",
    phone: "+223 76 99 88 77",
    email: null,
    address: "ACI 2000",
    city: "Bamako",
    notes: "Benjamin de Djeneba. Apprenti coiffeur.",
    spouseId: null,
    parentIds: ["djeneba", "bakary"],
  },
  {
    id: "lassana",
    firstName: "Lassana",
    lastName: "Traoré",
    birthYear: 1986,
    deathYear: null,
    gender: "male",
    phone: "+223 77 12 34 56",
    email: null,
    address: "Kalaban-Coura",
    city: "Bamako",
    notes: "Fils d'Issaka. Cousin au premier degré.",
    spouseId: "fatoumataL",
    parentIds: ["issaka", "assetouKonate"],
  },
  {
    id: "mariamPetite",
    firstName: "Mariam",
    lastName: "Traoré",
    birthYear: 1988,
    deathYear: null,
    gender: "female",
    phone: "+223 65 43 21 09",
    email: "mariam.t.k@gmail.com",
    address: "Kalaban-Coura",
    city: "Bamako",
    notes: "Fille d'Issaka. Infirmière.",
    spouseId: "bachir",
    parentIds: ["issaka", "assetouKonate"],
  },
  {
    id: "ousmane",
    firstName: "Ousmane",
    lastName: "Traoré",
    birthYear: 1991,
    deathYear: null,
    gender: "male",
    phone: "+223 76 88 77 66",
    email: null,
    address: "Kalaban-Coura",
    city: "Bamako",
    notes: "Fils d'Issaka. Chauffeur poids lourd.",
    spouseId: null,
    parentIds: ["issaka", "assetouKonate"],
  },
  {
    id: "salif",
    firstName: "Salif",
    lastName: "Traoré",
    birthYear: 1987,
    deathYear: null,
    gender: "male",
    phone: "+223 78 55 44 33",
    email: "salif.t@hotmail.com",
    address: "Sotuba",
    city: "Bamako",
    notes: "Fils de Fanta. Cousin au premier degré.",
    spouseId: "nouh",
    parentIds: ["fanta", "modibo"],
  },
  {
    id: "aissata",
    firstName: "Aïssata",
    lastName: "Traoré",
    birthYear: 1990,
    deathYear: null,
    gender: "female",
    phone: "+223 66 11 22 33",
    email: null,
    address: "Sotuba",
    city: "Bamako",
    notes: "Fille de Fanta. Coiffeuse.",
    spouseId: null,
    parentIds: ["fanta", "modibo"],
  },
  {
    id: "kadidia",
    firstName: "Kadidia",
    lastName: "Coulibaly",
    birthYear: 2005,
    deathYear: null,
    gender: "female",
    phone: null,
    email: null,
    address: "Badalabougou",
    city: "Bamako",
    notes: "Lycéenne.",
    spouseId: null,
    parentIds: ["fatima", "drissa"],
  },
  {
    id: "adama",
    firstName: "Adama",
    lastName: "Coulibaly",
    birthYear: 2007,
    deathYear: null,
    gender: "male",
    phone: null,
    email: null,
    address: "Badalabougou",
    city: "Bamako",
    notes: "Collégien.",
    spouseId: null,
    parentIds: ["fatima", "drissa"],
  },
  {
    id: "ibrahim2",
    firstName: "Ibrahim",
    lastName: "Traoré",
    birthYear: 2010,
    deathYear: null,
    gender: "male",
    phone: null,
    email: null,
    address: "Quinzambougou",
    city: "Bamako",
    notes: "Écolier.",
    spouseId: null,
    parentIds: ["moussa", "aminata"],
  },
  // Conjoint·e·s (branches élargies)
  {
    id: "aicha",
    firstName: "Aïcha",
    lastName: "Diallo",
    birthYear: 1981,
    deathYear: null,
    gender: "female",
    phone: "+223 66 70 80 90",
    email: null,
    address: "Magnambougou",
    city: "Bamako",
    notes: "Épouse d'Omar. Notaire.",
    spouseId: "omar",
    parentIds: [],
  },
  {
    id: "karim",
    firstName: "Karim",
    lastName: "Sangaré",
    birthYear: 1979,
    deathYear: null,
    gender: "male",
    phone: "+223 76 20 30 40",
    email: "karim.s@gmail.com",
    address: "Lafiabougou",
    city: "Bamako",
    notes: "Époux de Safiatou. Anesthésiste.",
    spouseId: "safiatou",
    parentIds: [],
  },
  {
    id: "idrissaB",
    firstName: "Idrissa",
    lastName: "Bah",
    birthYear: 1981,
    deathYear: null,
    gender: "male",
    phone: "+223 78 40 50 60",
    email: null,
    address: "Hamdallaye",
    city: "Bamako",
    notes: "Époux de Binta. Banquier.",
    spouseId: "binta",
    parentIds: [],
  },
  {
    id: "ramatou",
    firstName: "Ramatou",
    lastName: "Ouattara",
    birthYear: 1986,
    deathYear: null,
    gender: "female",
    phone: "+223 65 60 70 80",
    email: null,
    address: "Magnambougou",
    city: "Bamako",
    notes: "Épouse de Youssouf. Hôtesse de l'air.",
    spouseId: "youssouf",
    parentIds: [],
  },
  {
    id: "mamoudou",
    firstName: "Mamoudou",
    lastName: "Tamboura",
    birthYear: 1987,
    deathYear: null,
    gender: "male",
    phone: "+223 77 10 20 30",
    email: null,
    address: "Hippodrome",
    city: "Bamako",
    notes: "Époux de Hawa. Réalisateur.",
    spouseId: "hawa",
    parentIds: [],
  },
  {
    id: "salimataT",
    firstName: "Salimata",
    lastName: "Tall",
    birthYear: 1993,
    deathYear: null,
    gender: "female",
    phone: "+223 66 90 12 34",
    email: null,
    address: "ACI 2000",
    city: "Bamako",
    notes: "Épouse de Cheick. Couturière.",
    spouseId: "cheick",
    parentIds: [],
  },
  {
    id: "assetouKen",
    firstName: "Assetou",
    lastName: "Sow",
    birthYear: 1991,
    deathYear: null,
    gender: "female",
    phone: "+223 76 40 50 60",
    email: null,
    address: "ACI 2000",
    city: "Bamako",
    notes: "Épouse de Kenedi. Agronome.",
    spouseId: "kenedi",
    parentIds: [],
  },
  {
    id: "fatoumataL",
    firstName: "Fatoumata",
    lastName: "Coulibaly",
    birthYear: 1988,
    deathYear: null,
    gender: "female",
    phone: "+223 78 70 80 90",
    email: null,
    address: "Kalaban-Coura",
    city: "Bamako",
    notes: "Épouse de Lassana. Gestionnaire.",
    spouseId: "lassana",
    parentIds: [],
  },
  {
    id: "nouh",
    firstName: "Nouh",
    lastName: "Diaby",
    birthYear: 1989,
    deathYear: null,
    gender: "female",
    phone: "+223 65 50 60 70",
    email: null,
    address: "Sotuba",
    city: "Bamako",
    notes: "Épouse de Salif. Professeure de maths.",
    spouseId: "salif",
    parentIds: [],
  },
  {
    id: "bachir",
    firstName: "Bachir",
    lastName: "Touré",
    birthYear: 1987,
    deathYear: null,
    gender: "male",
    phone: "+223 76 30 40 50",
    email: null,
    address: "Kalaban-Coura",
    city: "Bamako",
    notes: "Époux de Mariam (fille d'Issaka). Plombier.",
    spouseId: "mariamPetite",
    parentIds: [],
  },
  {
    id: "issaRokia",
    firstName: "Issa",
    lastName: "Diarra",
    birthYear: 1990,
    deathYear: null,
    gender: "male",
    phone: "+223 77 60 70 80",
    email: "issa.d@gmail.com",
    address: "ACI 2000",
    city: "Bamako",
    notes: "Époux de Rokia. Développeur web.",
    spouseId: "rokia",
    parentIds: [],
  },
  // Petits-enfants & arrière-gen (cousins au second degré, etc.)
  {
    id: "yacouba",
    firstName: "Yacouba",
    lastName: "Traoré",
    birthYear: 2015,
    deathYear: null,
    gender: "male",
    phone: null,
    email: null,
    address: "Magnambougou",
    city: "Bamako",
    notes: "Fils d'Omar et Aïcha.",
    spouseId: null,
    parentIds: ["omar", "aicha"],
  },
  {
    id: "marieme",
    firstName: "Marième",
    lastName: "Traoré",
    birthYear: 2018,
    deathYear: null,
    gender: "female",
    phone: null,
    email: null,
    address: "Magnambougou",
    city: "Bamako",
    notes: "Fille d'Omar et Aïcha.",
    spouseId: null,
    parentIds: ["omar", "aicha"],
  },
  {
    id: "nadia",
    firstName: "Nadia",
    lastName: "Traoré",
    birthYear: 2012,
    deathYear: null,
    gender: "female",
    phone: null,
    email: null,
    address: "Lafiabougou",
    city: "Bamako",
    notes: "Fille de Safiatou et Karim.",
    spouseId: null,
    parentIds: ["safiatou", "karim"],
  },
  {
    id: "hassane",
    firstName: "Hassane",
    lastName: "Bah",
    birthYear: 2016,
    deathYear: null,
    gender: "male",
    phone: null,
    email: null,
    address: "Hamdallaye",
    city: "Bamako",
    notes: "Fils de Binta et Idrissa.",
    spouseId: null,
    parentIds: ["binta", "idrissaB"],
  },
  {
    id: "zaynab",
    firstName: "Zaynab",
    lastName: "Traoré",
    birthYear: 2014,
    deathYear: null,
    gender: "female",
    phone: null,
    email: null,
    address: "Magnambougou",
    city: "Bamako",
    notes: "Fille de Youssouf et Ramatou.",
    spouseId: null,
    parentIds: ["youssouf", "ramatou"],
  },
  {
    id: "bilal",
    firstName: "Bilal",
    lastName: "Traoré",
    birthYear: 2017,
    deathYear: null,
    gender: "male",
    phone: null,
    email: null,
    address: "Magnambougou",
    city: "Bamako",
    notes: "Fils de Youssouf et Ramatou.",
    spouseId: null,
    parentIds: ["youssouf", "ramatou"],
  },
  {
    id: "zeinabou",
    firstName: "Zeïnabou",
    lastName: "Tamboura",
    birthYear: 2019,
    deathYear: null,
    gender: "female",
    phone: null,
    email: null,
    address: "Hippodrome",
    city: "Bamako",
    notes: "Fille de Hawa et Mamoudou.",
    spouseId: null,
    parentIds: ["hawa", "mamoudou"],
  },
  {
    id: "moussaCheick",
    firstName: "Moussa",
    lastName: "Traoré",
    birthYear: 2021,
    deathYear: null,
    gender: "male",
    phone: null,
    email: null,
    address: "ACI 2000",
    city: "Bamako",
    notes: "Fils de Cheick et Salimata.",
    spouseId: null,
    parentIds: ["cheick", "salimataT"],
  },
  {
    id: "awaKen",
    firstName: "Awa",
    lastName: "Diallo",
    birthYear: 2020,
    deathYear: null,
    gender: "female",
    phone: null,
    email: null,
    address: "ACI 2000",
    city: "Bamako",
    notes: "Fille de Kenedi et Assetou.",
    spouseId: null,
    parentIds: ["kenedi", "assetouKen"],
  },
  {
    id: "ibrahimLassana",
    firstName: "Ibrahim",
    lastName: "Traoré",
    birthYear: 2016,
    deathYear: null,
    gender: "male",
    phone: null,
    email: null,
    address: "Kalaban-Coura",
    city: "Bamako",
    notes: "Fils de Lassana et Fatoumata.",
    spouseId: null,
    parentIds: ["lassana", "fatoumataL"],
  },
  {
    id: "oumou",
    firstName: "Oumou",
    lastName: "Traoré",
    birthYear: 2013,
    deathYear: null,
    gender: "female",
    phone: null,
    email: null,
    address: "Sotuba",
    city: "Bamako",
    notes: "Fille de Salif et Nouh.",
    spouseId: null,
    parentIds: ["salif", "nouh"],
  },
  {
    id: "rakiat",
    firstName: "Rakiat",
    lastName: "Coulibaly",
    birthYear: 2011,
    deathYear: null,
    gender: "female",
    phone: null,
    email: null,
    address: "Badalabougou",
    city: "Bamako",
    notes: "Cadette de Fatima et Drissa.",
    spouseId: null,
    parentIds: ["fatima", "drissa"],
  },
  {
    id: "balkissa",
    firstName: "Balkissa",
    lastName: "Traoré",
    birthYear: 2014,
    deathYear: null,
    gender: "female",
    phone: null,
    email: null,
    address: "Quinzambougou",
    city: "Bamako",
    notes: "Fille de Moussa et Aminata.",
    spouseId: null,
    parentIds: ["moussa", "aminata"],
  },
  {
    id: "hafsa",
    firstName: "Hafsa",
    lastName: "Touré",
    birthYear: 2015,
    deathYear: null,
    gender: "female",
    phone: null,
    email: null,
    address: "Kalaban-Coura",
    city: "Bamako",
    notes: "Fille de Mariam (Issaka) et Bachir.",
    spouseId: null,
    parentIds: ["mariamPetite", "bachir"],
  },
  {
    id: "lamine",
    firstName: "Lamine",
    lastName: "Traoré",
    birthYear: 2012,
    deathYear: null,
    gender: "male",
    phone: null,
    email: null,
    address: "Sotuba",
    city: "Bamako",
    notes: "Fils d'Aïssata (branche Fanta) — mère seule.",
    spouseId: null,
    parentIds: ["aissata"],
  },
  {
    id: "ousmanePetit",
    firstName: "Ousmane",
    lastName: "Diallo",
    birthYear: 2022,
    deathYear: null,
    gender: "male",
    phone: null,
    email: null,
    address: "ACI 2000",
    city: "Bamako",
    notes: "Fils d'Ousmane (Issaka) et compagne.",
    spouseId: null,
    parentIds: ["ousmane"],
  },
  {
    id: "fantaJunior",
    firstName: "Fanta",
    lastName: "Diarra",
    birthYear: 2023,
    deathYear: null,
    gender: "female",
    phone: null,
    email: null,
    address: "ACI 2000",
    city: "Bamako",
    notes: "Fille de Rokia et Issa.",
    spouseId: null,
    parentIds: ["rokia", "issaRokia"],
  },
];

/* ── Module-level people reference ─────────────────────────────
 * Kept in sync with the Zustand store via `setPeopleRef()`.
 * All internal family.js functions read `_people` so their
 * signatures don't need a `people` parameter.
 */
let _people = DEMO_PEOPLE;

/** Replace the active people array (called by the store). */
export function setPeopleRef(arr) { _people = arr; }
/** Read the current active people array. */
export function getPeopleRef() { return _people; }

export const getPerson = (id) => _people.find((p) => p.id === id);

export const getInitials = (person) =>
  `${person.firstName[0]}${person.lastName[0]}`.toUpperCase();

/** Legal full name: first, optional middle, last. */
export function getFullName(person) {
  if (!person) return "";
  const mid = typeof person.middleName === "string" ? person.middleName.trim() : "";
  const parts = [person.firstName, mid || null, person.lastName].filter(Boolean);
  return parts.join(" ");
}

/** First + optional middle (legal given names), no nickname substitution. */
export function getLegalGivenNames(person) {
  if (!person) return "";
  const first = String(person.firstName ?? "").trim();
  const mid = typeof person.middleName === "string" ? person.middleName.trim() : "";
  if (!first) return mid;
  return mid ? `${first} ${mid}` : first;
}

/** First name for lists/profile where the nickname replaces the legal first name when set. */
export function getDisplayFirstName(person) {
  if (!person) return "";
  const n = typeof person.nickname === "string" ? person.nickname.trim() : "";
  if (n) return n;
  return person.firstName ?? "";
}

/** Tree node first line: legal given name(s), plus ` (surnom)` when a nickname exists. */
export function getTreeNodeFirstNameLine(person) {
  if (!person) return "";
  const given = getLegalGivenNames(person);
  const nick = typeof person.nickname === "string" ? person.nickname.trim() : "";
  if (nick) return given ? `${given} (${nick})` : `(${nick})`;
  return given;
}

/** « Surnom » ou prénom + nom — libellé court pour fil d’Ariane, recherche, listes. */
export function getUsualName(person) {
  if (!person) return "";
  return `${getDisplayFirstName(person)} ${person.lastName}`.trim();
}

/**
 * Value for `<input type="date">`: stored `birthDate`, or YYYY-01-01 from `birthYear` when only the year exists (legacy / partial data).
 */
export function getBirthDateInputValue(person) {
  if (!person) return "";
  if (person.birthDate) return String(person.birthDate).trim().slice(0, 10);
  if (person.birthYear != null) {
    const y = Math.trunc(Number(person.birthYear));
    if (y >= 1 && y <= 9999) return `${String(y).padStart(4, "0")}-01-01`;
  }
  return "";
}

const BIRTH_DATE_NOON = "T12:00:00";

/** @param {string|undefined|null} isoDate YYYY-MM-DD */
function parseBirthDateMs(isoDate) {
  if (isoDate == null || typeof isoDate !== "string") return null;
  const ymd = isoDate.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const t = Date.parse(`${ymd}${BIRTH_DATE_NOON}`);
  return Number.isNaN(t) ? null : t;
}

/** Compact birth line for pickers / lists (locale-aware). */
export function getBirthShortLabel(person, lang = "fr") {
  if (!person) return "–";
  const dMs = parseBirthDateMs(person.birthDate);
  if (dMs != null) {
    return new Date(dMs).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });
  }
  if (person.birthYear != null) return String(person.birthYear);
  return "–";
}

/** Sort key for birth order (lower = earlier). */
export function getBirthSortKey(person) {
  const dMs = person?.birthDate ? parseBirthDateMs(person.birthDate) : null;
  if (dMs != null) return dMs;
  return (person?.birthYear ?? 9999) * 365.25 * 86400000;
}

/**
 * Human-readable birth (and death year when present).
 * @param {string} [lang] UI language: 'fr' | 'en'
 */
export function getLifespan(person, lang = "fr") {
  if (!person) return "—";
  const en = lang === "en";

  if (person.deathYear != null) {
    const dMs = parseBirthDateMs(person.birthDate);
    if (dMs != null) {
      const birthStr = new Date(dMs).toLocaleDateString(en ? "en-US" : "fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return `${birthStr} – ${person.deathYear}`;
    }
    if (person.birthYear != null) {
      return `${person.birthYear} – ${person.deathYear}`;
    }
    return en ? `d. ${person.deathYear}` : `† ${person.deathYear}`;
  }

  const dMs = parseBirthDateMs(person.birthDate);
  if (dMs != null) {
    return new Date(dMs).toLocaleDateString(en ? "en-US" : "fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  if (person.birthYear != null) {
    return en
      ? `b. ${person.birthYear}`
      : `né${person.gender === "female" ? "e" : ""} en ${person.birthYear}`;
  }

  return "—";
}

/**
 * Person cards — used by layout (`buildTreeLayout`) and as the "full" size.
 * `PERSON_NODE_SIZE_COMPACT` is used in zoomed-out + `PersonNode` + `FamilyConnectors`.
 * @see getPersonNodeSizeAtZoom
 */
export const ZOOM_COMPACT_CARDS = 0.65;
export const PERSON_NODE_SIZE_FULL = { width: 176, height: 136 };
export const PERSON_NODE_SIZE_COMPACT = { width: 176, height: 90 };
/** Layout spacing always uses the larger (full) box so nodes never overlap per generation. */
export const PERSON_NODE_SIZE = PERSON_NODE_SIZE_FULL;

export function getPersonNodeSizeAtZoom(z) {
  return (z < ZOOM_COMPACT_CARDS ? PERSON_NODE_SIZE_COMPACT : PERSON_NODE_SIZE_FULL);
}

/**
 * Stroke styles for `FamilyConnectors` — import the same object in `TreeCanvas`
 * so the legend matches the graph pixel-for-pixel.
 */
export const FAMILY_CONNECTOR_STYLES = {
  /** Parent–child links (drop, bus bar, legs, or single-child curve). */
  descent: {
    stroke: "rgba(40,32,20,0.28)",
    strokeWidth: 1.5,
  },
  /** Spouse link (horizontal dashed bar between partners). */
  couple: {
    stroke: "rgba(201,146,42,0.7)",
    strokeWidth: 2,
    strokeDasharray: "5 4",
  },
  /** Former union / ex-spouse — same layout, cooler, longer dashes. */
  coupleFormer: {
    stroke: "rgba(100,95,88,0.55)",
    strokeWidth: 1.75,
    strokeDasharray: "2 6",
  },
  /** Stand-alone adoptive parent→child (when no main family bus covers this edge). */
  adoptiveDescent: {
    stroke: "rgba(13, 148, 136, 0.5)",
    strokeWidth: 1.5,
    strokeDasharray: "4 4",
  },
};

/** Normalize union_status from data (DB, demo seed, or legacy spouseId only). */
export function unionEntryStatus(u) {
  if (!u) return "married";
  return u.unionStatus === "former" ? "former" : "married";
}

/**
 * Marital unions (polygyny / monogamy). Prefer `unions` when present; otherwise
 * a single `spouseId` is treated as one implicit union (current).
 */
export function resolveUnions(person) {
  if (!person) return [];
  if (person.unions?.length) {
    return [...person.unions]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((u) => ({
        ...u,
        unionStatus: unionEntryStatus(u),
      }));
  }
  if (person.spouseId) {
    return [{ spouseId: person.spouseId, order: 0, unionStatus: "married" }];
  }
  return [];
}

export function getUnionBetween(aId, bId) {
  const a = getPerson(aId);
  if (!a) return null;
  return resolveUnions(a).find((u) => u.spouseId === bId) ?? null;
}

/** `'married' | 'former'` for the edge between two people if a union exists. */
export function unionStatusBetween(aId, bId) {
  const u = getUnionBetween(aId, bId) || getUnionBetween(bId, aId);
  return unionEntryStatus(u);
}

/** All co-parent / spouse person IDs. */
export function getSpouseIds(person) {
  return resolveUnions(person).map((u) => u.spouseId).filter(Boolean);
}

export function isAdoptiveParentLink(child, parentId) {
  return child?.parentLinkTypes?.[parentId] === "adoptive";
}

/**
 * Describe how `toId` is related to `fromId`, for the relationship pill on nodes.
 * Returns `{ key, fr }` for i18n (`key` is used with `useT`) or `null`.
 */
export function getRelationshipLabelParts(fromId, toId) {
  if (!fromId || !toId || fromId === toId) return null;
  const from = getPerson(fromId);
  const to = getPerson(toId);
  if (!from || !to) return null;

  const female = to.gender === "female";
  const parentsOf = (id) => getPerson(id)?.parentIds ?? [];
  const siblingsOf = (id) => {
    const pids = parentsOf(id);
    if (!pids.length) return [];
    return _people.filter(
      (p) => p.id !== id && p.parentIds.some((pid) => pids.includes(pid))
    );
  };

  const spouseLabel = (former) => {
    if (former) {
      return female
        ? { key: "rel.exWife", fr: "Ex-épouse" }
        : { key: "rel.exHusband", fr: "Ex-époux" };
    }
    return female
      ? { key: "rel.wife", fr: "Épouse" }
      : { key: "rel.husband", fr: "Époux" };
  };

  if (getSpouseIds(from).includes(toId)) {
    return spouseLabel(unionStatusBetween(fromId, toId) === "former");
  }
  if (getSpouseIds(to).includes(fromId)) {
    return spouseLabel(unionStatusBetween(fromId, toId) === "former");
  }

  if (from.parentIds.includes(toId)) {
    if (isAdoptiveParentLink(from, toId)) {
      return female
        ? { key: "rel.adoptiveMother", fr: "Mère adoptive" }
        : { key: "rel.adoptiveFather", fr: "Père adoptif" };
    }
    return female
      ? { key: "rel.mother", fr: "Mère" }
      : { key: "rel.father", fr: "Père" };
  }
  if (to.parentIds.includes(fromId)) {
    if (isAdoptiveParentLink(to, fromId)) {
      return female
        ? { key: "rel.adoptiveDaughter", fr: "Fille adoptive" }
        : { key: "rel.adoptiveSon", fr: "Fils adoptif" };
    }
    return female
      ? { key: "rel.daughter", fr: "Fille" }
      : { key: "rel.son", fr: "Fils" };
  }

  if (from.parentIds.length && to.parentIds.length) {
    const a = [...from.parentIds].sort().join("|");
    const b = [...to.parentIds].sort().join("|");
    if (a === b)
      return female
        ? { key: "rel.sister", fr: "Sœur" }
        : { key: "rel.brother", fr: "Frère" };
    const fromP = new Set(from.parentIds);
    const toP = new Set(to.parentIds);
    const nShared = [...fromP].filter((x) => toP.has(x)).length;
    if (nShared === 1)
      return female
        ? { key: "rel.halfSister", fr: "Demi-sœur" }
        : { key: "rel.halfBrother", fr: "Demi-frère" };
  }

  const fromGrandparents = new Set(from.parentIds.flatMap(parentsOf));
  if (fromGrandparents.has(toId))
    return female
      ? { key: "rel.grandmother", fr: "Grand-mère" }
      : { key: "rel.grandfather", fr: "Grand-père" };
  const toGrandparents = new Set(to.parentIds.flatMap(parentsOf));
  if (toGrandparents.has(fromId))
    return female
      ? { key: "rel.granddaughter", fr: "Petite-fille" }
      : { key: "rel.grandson", fr: "Petit-fils" };

  for (const pid of from.parentIds) {
    if (siblingsOf(pid).some((s) => s.id === toId)) {
      return female
        ? { key: "rel.aunt", fr: "Tante" }
        : { key: "rel.uncle", fr: "Oncle" };
    }
  }

  for (const pid of to.parentIds) {
    if (siblingsOf(pid).some((s) => s.id === fromId)) {
      return female
        ? { key: "rel.niece", fr: "Nièce" }
        : { key: "rel.nephew", fr: "Neveu" };
    }
  }

  for (const fromP of from.parentIds) {
    const fromPsibs = siblingsOf(fromP).map((s) => s.id);
    for (const toP of to.parentIds) {
      if (fromPsibs.includes(toP))
        return female
          ? { key: "rel.cousinF", fr: "Cousine" }
          : { key: "rel.cousinM", fr: "Cousin" };
    }
  }

  return null;
}

/** @deprecated Prefer getRelationshipLabelParts + i18n; returns French only. */
export function getRelationshipLabel(fromId, toId) {
  return getRelationshipLabelParts(fromId, toId)?.fr ?? null;
}

/**
 * Layout inspired by "Family Tree.html" — family-centric recursive width.
 *
 * Instead of drawing one edge per parent→child relationship (which blends
 * visually when many siblings share the same parents), we group people into
 * FAMILIES: { parents: [...], children: [...] }. Each family is rendered
 * later as a single "bus line" (couple bar → drop → horizontal bus →
 * vertical lines to each child).
 */

// Spacing (tweak here, rest follows)
const COUPLE_GAP = 48; // horizontal gap between two spouses
const COL_STEP = PERSON_NODE_SIZE.width + 72; // per-column width unit
const GEN_STEP = PERSON_NODE_SIZE.height + 120; // vertical distance per generation

/**
 * Build family groups from a set of visible person IDs.
 * Monogamie : { kind:'nuclear', parents, children }.
 * Polygynie (2+ unions sur une même ancre) : { kind:'polygynous', anchor, spouses, childrenBySpouse }.
 */
function buildFamilies(visibleIds) {
  const visible = new Set(visibleIds);
  const byKey = new Map();

  const orderParents = (ids) => {
    return [...ids].sort((a, b) => {
      const pa = getPerson(a);
      const pb = getPerson(b);
      const aHasLineage = pa?.parentIds?.some((id) => visible.has(id)) ? 1 : 0;
      const bHasLineage = pb?.parentIds?.some((id) => visible.has(id)) ? 1 : 0;
      if (aHasLineage !== bHasLineage) return bHasLineage - aHasLineage;
      const ga = pa?.gender === "male" ? 0 : 1;
      const gb = pb?.gender === "male" ? 0 : 1;
      if (ga !== gb) return ga - gb;
      return a.localeCompare(b);
    });
  };

  for (const pid of visibleIds) {
    const p = getPerson(pid);
    if (!p?.parentIds?.length) continue;
    const parentsInView = p.parentIds.filter((id) => visible.has(id));
    if (!parentsInView.length) continue;
    const ordered = orderParents(parentsInView);
    const key = ordered.join("|");
    if (!byKey.has(key)) byKey.set(key, { parents: ordered, children: [] });
    byKey.get(key).children.push(pid);
  }

  const pairSeen = new Set();
  for (const pid of visibleIds) {
    const p = getPerson(pid);
    if (!p) continue;
    for (const sp of getSpouseIds(p)) {
      if (!visible.has(sp)) continue;
      const pair = orderParents([pid, sp]);
      const key = pair.join("|");
      if (pairSeen.has(key)) continue;
      pairSeen.add(key);
      if (!byKey.has(key)) byKey.set(key, { parents: pair, children: [] });
    }
  }

  for (const fam of byKey.values()) {
    fam.children.sort(
      (a, b) => (getPerson(a)?.birthYear ?? 0) - (getPerson(b)?.birthYear ?? 0)
    );
  }

  const polyFams = [];
  for (const p of _people) {
    if (resolveUnions(p).length < 2) continue;
    const aid = p.id;
    if (!visible.has(aid)) continue;
    const orderedUnions = resolveUnions(p);
    const inView = orderedUnions
      .map((u) => u.spouseId)
      .filter((sid) => visible.has(sid));
    if (inView.length < 2) continue;

    const childrenBySpouse = {};
    for (const u of orderedUnions) {
      if (!visible.has(u.spouseId)) continue;
      const k = orderParents([aid, u.spouseId]).join("|");
      const ent = byKey.get(k);
      childrenBySpouse[u.spouseId] = ent ? [...ent.children] : [];
    }
    for (const u of orderedUnions) {
      if (!visible.has(u.spouseId)) continue;
      const k = orderParents([aid, u.spouseId]).join("|");
      if (byKey.has(k)) byKey.delete(k);
    }
    const spouses = orderedUnions
      .map((u) => u.spouseId)
      .filter((sid) => visible.has(sid));
    for (const sid of spouses) {
      if (childrenBySpouse[sid] == null) childrenBySpouse[sid] = [];
      childrenBySpouse[sid].sort(
        (a, b) => (getPerson(a)?.birthYear ?? 0) - (getPerson(b)?.birthYear ?? 0)
      );
    }
    polyFams.push({
      id: `fam-poly-${aid}`,
      kind: "polygynous",
      anchor: aid,
      spouses,
      childrenBySpouse,
    });
  }

  // ── Explicit sibling groups (parentless fraternal links) ──────
  // People connected by siblingIds who don't already share a family group
  // through common parents get grouped into a "sibling" family with no parents.
  const alreadyGrouped = new Set();
  for (const fam of byKey.values()) {
    for (const cid of fam.children) alreadyGrouped.add(cid);
  }
  for (const pfam of polyFams) {
    for (const kids of Object.values(pfam.childrenBySpouse)) {
      for (const cid of kids) alreadyGrouped.add(cid);
    }
  }

  // Build connected components from explicit siblingIds using union-find
  const sibParent = new Map();
  const sibFind = (x) => {
    if (!sibParent.has(x)) sibParent.set(x, x);
    while (sibParent.get(x) !== x) {
      sibParent.set(x, sibParent.get(sibParent.get(x)));
      x = sibParent.get(x);
    }
    return x;
  };
  const sibUnion = (a, b) => {
    const ra = sibFind(a), rb = sibFind(b);
    if (ra !== rb) sibParent.set(ra, rb);
  };

  for (const pid of visibleIds) {
    const p = getPerson(pid);
    if (!p?.siblingIds?.length) continue;
    for (const sid of p.siblingIds) {
      if (!visible.has(sid)) continue;
      // Only group them here if at least one is NOT already placed by parent links
      if (alreadyGrouped.has(pid) && alreadyGrouped.has(sid)) continue;
      sibUnion(pid, sid);
    }
  }

  // Collect sibling groups
  const sibGroups = new Map();
  for (const pid of visibleIds) {
    if (!sibParent.has(pid)) continue;
    const root = sibFind(pid);
    if (!sibGroups.has(root)) sibGroups.set(root, []);
    sibGroups.get(root).push(pid);
  }

  let ni = 0;
  const nuclearFams = [...byKey.values()].map((f) => ({
    id: `fam-n-${ni++}`,
    kind: "nuclear",
    parents: f.parents,
    children: f.children,
    coupleUnionStatus:
      f.parents?.length === 2
        ? unionStatusBetween(f.parents[0], f.parents[1])
        : null,
  }));

  // Create parentless sibling families
  let si = 0;
  const siblingFams = [];
  for (const members of sibGroups.values()) {
    if (members.length < 2) continue;
    // Sort by birth year for consistent ordering
    members.sort(
      (a, b) => (getPerson(a)?.birthYear ?? 0) - (getPerson(b)?.birthYear ?? 0)
    );
    siblingFams.push({
      id: `fam-sib-${si++}`,
      kind: "nuclear",
      parents: [],
      children: members,
      coupleUnionStatus: null,
    });
  }

  return [...polyFams, ...nuclearFams, ...siblingFams];
}

/**
 * True when the main family connector (couple bar → descent bus) already draws
 * this parent→child. Used to skip duplicate lines for adoptive links.
 */
export function familyBusConnectsParentToChild(parentId, childId, families) {
  if (!families?.length || !parentId || !childId) return false;
  for (const fam of families) {
    if (fam.kind === "polygynous") {
      for (const sid of fam.spouses || []) {
        const kids = fam.childrenBySpouse[sid] || [];
        if (!kids.includes(childId)) continue;
        if (parentId === fam.anchor || parentId === sid) return true;
      }
    } else {
      if (!fam.parents?.includes(parentId)) continue;
      if (fam.children?.includes(childId)) return true;
    }
  }
  return false;
}

/**
 * dagre-based hierarchical layout.
 *
 * Strategy:
 *   1. Build a dagre graph with each person as a node and parent→child as edges.
 *   2. Spouses are merged into "couple compound nodes" so dagre keeps them on the
 *      same rank. After dagre runs we split them back into side-by-side positions.
 *   3. Polygynous families use a wider compound (anchor + N spouses).
 *
 * This replaces the old hand-rolled recursive layout while keeping the same
 * output shape ({ positions, famByChild, famByParent }) for FamilyConnectors.
 */
function computeFamilyLayout(visibleIds, families) {
  const NW = PERSON_NODE_SIZE.width;
  const NH = PERSON_NODE_SIZE.height;

  // ── Build lookup maps (needed by FamilyConnectors + caller) ──
  const famByChild = new Map();
  const famByParent = new Map();
  for (const f of families) {
    if (f.kind === "polygynous") {
      famByParent.set(f.anchor, f);
      f.spouses.forEach((s) => famByParent.set(s, f));
      for (const kids of Object.values(f.childrenBySpouse)) {
        for (const c of kids) famByChild.set(c, f);
      }
    } else {
      f.parents?.forEach((p) => famByParent.set(p, f));
      (f.children || []).forEach((c) => famByChild.set(c, f));
    }
  }

  // ── Identify couple groups (spouses that should share a rank) ──
  // coupleGroupOf: personId → groupId,  coupleGroups: groupId → [personIds]
  const coupleGroupOf = new Map();
  const coupleGroups = new Map();
  let groupCounter = 0;

  for (const fam of families) {
    if (fam.kind === "polygynous") {
      const members = [fam.spouses[0], fam.anchor, ...fam.spouses.slice(1)];
      const gid = `cg-${groupCounter++}`;
      coupleGroups.set(gid, members);
      members.forEach((id) => coupleGroupOf.set(id, gid));
    } else if (fam.parents?.length === 2) {
      const [a, b] = fam.parents;
      // If either is already in a poly group, skip
      if (coupleGroupOf.has(a) || coupleGroupOf.has(b)) continue;
      const gid = `cg-${groupCounter++}`;
      coupleGroups.set(gid, [a, b]);
      coupleGroupOf.set(a, gid);
      coupleGroupOf.set(b, gid);
    }
  }

  // ── Build dagre graph ──
  // Couple groups become single compound nodes; solo people are individual nodes.
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: "TB",
    nodesep: 72,
    ranksep: GEN_STEP - NH,
    marginx: 20,
    marginy: 20,
  });
  g.setDefaultEdgeLabel(() => ({}));

  const visibleSet = new Set(visibleIds);
  const nodeInGraph = new Set(); // track which dagre node each person maps to

  // Add compound couple nodes
  for (const [gid, members] of coupleGroups) {
    const validMembers = members.filter((id) => visibleSet.has(id));
    if (validMembers.length === 0) continue;
    const compoundW = validMembers.length * NW + (validMembers.length - 1) * COUPLE_GAP;
    g.setNode(gid, { width: compoundW, height: NH });
    validMembers.forEach((id) => nodeInGraph.add(id));
  }

  // Add solo nodes (people not in any couple group)
  for (const id of visibleIds) {
    if (nodeInGraph.has(id)) continue;
    g.setNode(id, { width: NW, height: NH });
    nodeInGraph.add(id);
  }

  // Helper: which dagre node represents a person?
  const dagreNodeFor = (id) => coupleGroupOf.get(id) ?? id;

  // Add edges: parent → child
  const edgeSet = new Set();
  for (const fam of families) {
    const allChildren = fam.kind === "polygynous"
      ? Object.values(fam.childrenBySpouse).flat()
      : (fam.children || []);
    const allParents = fam.kind === "polygynous"
      ? [fam.anchor, ...fam.spouses]
      : (fam.parents || []);

    // Use the compound node of any parent as the edge source
    const srcSet = new Set(allParents.map(dagreNodeFor));

    if (srcSet.size > 0) {
      for (const src of srcSet) {
        for (const cid of allChildren) {
          if (!visibleSet.has(cid)) continue;
          const dst = dagreNodeFor(cid);
          if (src === dst) continue;
          const ek = `${src}→${dst}`;
          if (edgeSet.has(ek)) continue;
          edgeSet.add(ek);
          g.setEdge(src, dst);
        }
      }
    } else if (allChildren.length >= 2) {
      // Parentless sibling group: insert a zero-size virtual anchor node
      // and draw edges from it to each sibling so dagre places them on
      // the same rank (one level below the anchor).
      const anchorId = `__sib_anchor_${fam.id}`;
      g.setNode(anchorId, { width: 0, height: 0 });
      for (const cid of allChildren) {
        if (!visibleSet.has(cid)) continue;
        const dst = dagreNodeFor(cid);
        const ek = `${anchorId}→${dst}`;
        if (edgeSet.has(ek)) continue;
        edgeSet.add(ek);
        g.setEdge(anchorId, dst);
      }
    }
  }

  // ── Run dagre ──
  dagre.layout(g);

  // ── Extract positions ──
  const positions = {};

  // Expand compound couple nodes into individual positions
  for (const [gid, members] of coupleGroups) {
    const node = g.node(gid);
    if (!node) continue;
    const validMembers = members.filter((id) => visibleSet.has(id));
    const count = validMembers.length;
    if (count === 0) continue;

    const totalW = count * NW + (count - 1) * COUPLE_GAP;
    const startX = node.x - totalW / 2;
    validMembers.forEach((id, i) => {
      positions[id] = {
        x: startX + i * (NW + COUPLE_GAP),
        y: node.y - NH / 2,
      };
    });
  }

  // Solo nodes
  for (const id of visibleIds) {
    if (positions[id]) continue;
    const node = g.node(id);
    if (node) {
      positions[id] = { x: node.x - NW / 2, y: node.y - NH / 2 };
    }
  }

  // Fallback: any visible person still without a position
  visibleIds.forEach((id, i) => {
    if (!positions[id]) {
      positions[id] = { x: i * COL_STEP, y: 0 };
    }
  });

  // Center the layout around x = 0
  const xs = Object.values(positions).map((p) => p.x + NW / 2);
  if (xs.length) {
    const mid = (Math.min(...xs) + Math.max(...xs)) / 2;
    Object.values(positions).forEach((p) => (p.x -= mid));
  }

  return { positions, famByChild, famByParent };
}

/**
 * Collect a relevant subset of people around `rootId`:
 *   - all ancestors up to 2 generations
 *   - all descendants of ancestors (→ siblings, uncles, aunts, cousins …)
 *   - every visible person's spouse
 */
function collectVisible(rootId) {
  const visited = new Set();
  const enqueue = (id) => {
    if (!id || visited.has(id)) return;
    const p = getPerson(id);
    if (!p) return;
    visited.add(id);
  };

  enqueue(rootId);

  // ancestors up 2 gens
  const ancestorSeeds = [rootId];
  for (let depth = 0; depth < 3 && ancestorSeeds.length; depth++) {
    const next = [];
    ancestorSeeds.forEach((id) => {
      const p = getPerson(id);
      p?.parentIds?.forEach((pid) => {
        if (!visited.has(pid)) {
          enqueue(pid);
          next.push(pid);
        }
      });
    });
    ancestorSeeds.length = 0;
    ancestorSeeds.push(...next);
  }

  // expand: parents, siblings, children, spouses — until stable
  for (let pass = 0; pass < 40; pass++) {
    let changed = false;

    // parents — every visible person's parents must also be visible so
    // connectors can be drawn. Without this, a newly added parent of a
    // non-root person would land in the orphan row with no connection.
    for (const id of [...visited]) {
      const p = getPerson(id);
      if (!p?.parentIds?.length) continue;
      for (const pid of p.parentIds) {
        if (!visited.has(pid) && getPerson(pid)) {
          enqueue(pid);
          changed = true;
        }
      }
    }

    // siblings (any person sharing at least one parent with a visible person)
    for (const person of _people) {
      if (visited.has(person.id)) continue;
      if (!person.parentIds?.length) continue;
      if (!person.parentIds.some((pid) => visited.has(pid))) continue;
      enqueue(person.id);
      changed = true;
    }

    // explicit siblings (siblingIds — works even without shared parents)
    for (const id of [...visited]) {
      const p = getPerson(id);
      if (!p?.siblingIds?.length) continue;
      for (const sid of p.siblingIds) {
        if (!visited.has(sid) && getPerson(sid)) {
          enqueue(sid);
          changed = true;
        }
      }
    }

    // spouses (toutes les unions) — but not spouses of collapsed nodes' hidden children
    for (const id of [...visited]) {
      const p = getPerson(id);
      for (const sp of getSpouseIds(p)) {
        if (sp && !visited.has(sp)) {
          enqueue(sp);
          changed = true;
        }
      }
    }

    // children (at least one parent in view)
    for (const child of _people) {
      if (visited.has(child.id)) continue;
      if (!child.parentIds?.length) continue;
      if (!child.parentIds.some((pid) => visited.has(pid))) continue;
      enqueue(child.id);
      changed = true;
    }

    if (!changed) break;
  }

  return [...visited];
}

/**
 * Main entry point — returns React Flow nodes, an empty edges list (we draw
 * our own connectors via <FamilyConnectors />), plus the family + position
 * metadata the connector overlay needs.
 */
export function buildTreeLayout(rootId) {
  const visibleIds = collectVisible(rootId);
  const families = buildFamilies(visibleIds);
  const { positions } = computeFamilyLayout(visibleIds, families);

  // Place any orphaned nodes (in tree but unreachable from root) in a row below
  const visibleSet = new Set(visibleIds);
  const orphans = _people.filter((p) => !visibleSet.has(p.id));
  if (orphans.length > 0) {
    const maxY = Object.values(positions).reduce((m, p) => Math.max(m, p.y), 0);
    const orphanY = maxY + PERSON_NODE_SIZE.height + GEN_STEP;
    orphans.forEach((p, i) => {
      positions[p.id] = { x: i * COL_STEP, y: orphanY };
    });
  }

  const allIds = [...visibleIds, ...orphans.map((p) => p.id)];

  const nodes = allIds
    .filter((id) => positions[id])
    .map((id) => ({
      id,
      type: "personNode",
      position: positions[id],
      data: { person: getPerson(id), isRoot: id === rootId },
      // Explicit size lets React Flow fit the viewport without waiting for DOM measurement
      width: PERSON_NODE_SIZE.width,
      height: PERSON_NODE_SIZE.height,
    }));

  return { nodes, edges: [], families, positions };
}
